import { motion } from "framer-motion";
import {
    Building2,
    School,
    University,
    Briefcase,
    Sparkles,
    Globe2,
} from "lucide-react";
import { TID } from "../../lib/testIds";

const audiences = [
    {
        icon: Building2,
        title: "Coaching Institutes",
        meta: "Batches • Test Series • Faculty",
    },
    {
        icon: School,
        title: "Schools",
        meta: "K-12 • Boards • Parent Portal",
    },
    {
        icon: University,
        title: "Universities",
        meta: "Departments • Credits • Research",
    },
    {
        icon: Briefcase,
        title: "Corporate Training",
        meta: "L&D • Certifications • Compliance",
    },
    {
        icon: Sparkles,
        title: "Skill Academies",
        meta: "Cohorts • Bootcamps • Mentors",
    },
    {
        icon: Globe2,
        title: "EdTech Platforms",
        meta: "APIs • Scale • White-label",
    },
];

const Card = ({ Icon, title, meta, idx }) => (
    <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{
            duration: 0.7,
            delay: idx * 0.06,
            ease: [0.16, 1, 0.3, 1],
        }}
        data-testid={TID.whoForCard}
        className="group relative rounded-2xl p-[1px] overflow-hidden"
    >
        {/* gradient border */}
        <div
            aria-hidden
            className="absolute inset-0 rounded-2xl opacity-40 group-hover:opacity-90 transition-opacity duration-500"
            style={{
                background:
                    "conic-gradient(from 140deg at 50% 50%, rgba(139,92,246,0.0) 0deg, rgba(139,92,246,0.55) 90deg, rgba(34,211,238,0.55) 180deg, rgba(139,92,246,0.0) 360deg)",
            }}
        />
        <div className="relative rounded-2xl bg-white dark:bg-[#06070F] p-7 h-full shadow-[0_2px_10px_rgba(0,0,0,0.04)] dark:shadow-none">
            {/* inner bloom on hover */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                    background:
                        "radial-gradient(450px circle at 50% 0%, rgba(139,92,246,0.10), transparent 60%)",
                }}
            />
            <div className="relative flex items-start gap-4">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-violet-50 border border-violet-100 group-hover:border-violet-300 group-hover:bg-violet-100 dark:bg-white/[0.04] dark:border-white/10 dark:group-hover:border-brand-purple/40 dark:group-hover:bg-brand-purple/10 transition-all">
                    <Icon size={20} className="text-violet-600 dark:text-white/90" />
                </div>
                <div className="pt-1">
                    <h3 className="font-display text-lg font-semibold tracking-tight text-[#111827] dark:text-white">
                        {title}
                    </h3>
                    <p className="mt-1 font-mono text-[11px] tracking-[0.18em] uppercase text-[#9CA3AF] dark:text-white/40">
                        {meta}
                    </p>
                </div>
            </div>
            {/* corner dot */}
            <span className="absolute top-4 right-4 w-1.5 h-1.5 rounded-full bg-violet-500/70 dark:bg-brand-purple/70 shadow-[0_0_10px_rgba(139,92,246,0.7)] opacity-60 group-hover:opacity-100 transition-opacity" />
        </div>
    </motion.div>
);

const WhoFor = () => (
    <section
        id="who-for"
        data-testid={TID.whoForSection}
        className="relative py-28 md:py-36 px-6"
    >
        <div className="max-w-7xl mx-auto relative z-10">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                <div className="max-w-2xl">
                    <p className="font-mono text-xs tracking-[0.4em] uppercase text-violet-500 dark:text-brand-blue/80">
                        02 — Who it&apos;s for
                    </p>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-gradient-brand">
                        Built for the institutions
                        <br className="hidden sm:block" /> shaping tomorrow.
                    </h2>
                </div>
                <p className="md:max-w-sm text-[#6B7280] dark:text-white/55 leading-relaxed">
                    From a single tuition center to a multi-campus university or
                    a national EdTech — Lambda Quest scales with you.
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {audiences.map((a, i) => (
                    <Card key={a.title} {...{ ...a, Icon: a.icon, idx: i }} />
                ))}
            </div>
        </div>
    </section>
);

export default WhoFor;
